import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core";
import { FormControl } from "@angular/forms";
import { NODE_TYPE } from "../../common/linqset-model";

export class GroupItem {
    constructor(public _key:string, public parentKey:string, public name:string, public image?:string, public selected:boolean=false, public type?:NODE_TYPE, public items?:GroupItem[]) {

    }
}

export interface ItemSelection {
    group:GroupItem
    item:GroupItem
}

// an option's value names its group as well as the item, so the same item in two groups can't be mixed up
const optionValue = (group:GroupItem, item:GroupItem) => `${group._key}\u0000${item._key}`

// a select list with its options in groups, allowing one choice or several.
// Single choice holds one value (or null); several choices hold a list of values
@Component({
    selector: 'app-group-selection',
    templateUrl: './group-selection.html',
    styleUrl: "./group-selection.css",
    standalone: false
})
export class GroupSelectionComponent implements OnChanges {
    @Input() groups:GroupItem[]
    @Input() hint:string
    @Input() label:string
    @Input() placeholder:string
    @Input() multiple:boolean
    @Input() clearable = false      // single choice only: offers None, which sends undefined

    @Output() selectedEvent:EventEmitter<any> = new EventEmitter<any>()
    @Output() openedEvent:EventEmitter<{open:boolean, selection:ItemSelection[]}> = new EventEmitter<{open:boolean, selection:ItemSelection[]}>()

    selectionControl = new FormControl<any>(null);

    private options = new Map<string, ItemSelection>()     // option value to its group and item

    constructor() {}

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['groups'] || changes['multiple']) && this.groups) {
            this.options.clear()
            const selected:string[] = []

            this.groups.forEach(g => {
                (g.items || []).forEach(i => {
                    const value = optionValue(g, i)
                    this.options.set(value, {group:g, item:i})
                    if (i.selected) selected.push(value)
                })
            })
            this.selectionControl.setValue(this.multiple ? selected : (selected[0] ?? null))
        }
    }

    optionValue(group:GroupItem, item:GroupItem) {
        return optionValue(group, item)
    }

    // the chosen options as a list, whichever mode the list is in
    private selectedValues():string[] {
        const value = this.selectionControl.value
        if (Array.isArray(value)) return value
        return value === null || value === undefined ? [] : [value]
    }

    private selection():ItemSelection[] {
        return this.selectedValues().map(v => this.options.get(v)).filter(Boolean)
    }

    get selectedCount() {
        return this.selectedValues().length
    }

    onOpenedChange(open:boolean) {
        this.openedEvent.emit({open, selection:this.selection()})
    }

    onSelectionChange() {
        const selection = this.selection()
        this.selectedEvent.emit(this.multiple ? selection : selection[0])
    }

    clearSelection() {
        this.selectionControl.setValue(this.multiple ? [] : null)
    }

    // chooses an item by its own key, as callers don't know which group it is in
    setSelection(key:string) {
        const value = [...this.options.entries()].find(([, o]) => o.item._key === key)?.[0]
        if (this.multiple) this.selectionControl.setValue(value ? [value] : [])
        else this.selectionControl.setValue(value ?? null)
    }

    getSelectionName() {
        return this.selection()[0]?.item.name || ""
    }
}
